// Locations list table. A kore DataTable with one column per fact: name, service
// status, when the service was last heard from, and which version it runs. The
// rows, the sort and the page all come from `/locations/search`, so this component
// only renders — the caller owns the request (see locations.index.tsx).
//
// Service health used to be one composite cell holding all three. Splitting it
// is what makes kore's per-column sorting and filtering usable — a version
// buried inside a cell with a badge and a timestamp can never answer "which
// locations are still on 0.1.2".
//
// Parse errors used to have a column here. It showed 0 in every normal
// operation, carried an "(approx.)" caveat, and duplicated a signal the
// Overview's Problems card already raises only when the count is non-zero —
// and with the last error message, which is the part worth reading. A
// malformed posting file needs a Simphony version change or someone editing
// the file by hand, so it is an exception to be alerted on, not a metric to
// watch.

import { DataTable, type DataTableColumn, type DataTablePagination, type DataTableSort } from "@pej/kore/components";
import { FormatRelativeTime } from "@pej/kore/intl";
import { useNavigate } from "@tanstack/react-router";
import type { LocationWithHealth } from "@/api/types";
import { sortableHeader } from "@/features/search/sortable-header";
import { useNow } from "@/lib/use-now";
import { ServiceStatusBadge, serviceHealthStatus } from "./service-health";

export interface LocationsTableProps {
    workspaceId: string;
    locations: LocationWithHealth[];
    isLoading?: boolean;
    /**
     * The request failed. Distinct from an empty result on purpose: an empty
     * table under a filter reads "nothing matched", which is a lie when what
     * actually happened is that the search never answered.
     */
    isError?: boolean;
    onRetry?: () => void;
    sort?: DataTableSort;
    onSortChange?: (next: DataTableSort | undefined) => void;
    /** Column keys the server declares sortable. Default: none. */
    sortableColumnKeys?: ReadonlySet<string>;
    pagination?: DataTablePagination;
    /** Shown when a search or filter is what emptied the table. */
    emptyBecauseFiltered?: boolean;
    /** Merged into kore's DataTable root. The list page passes
     *  `min-h-0 flex-1` so the table fills the viewport and scrolls its own
     *  rows; the decision stays at the page rather than being baked in here,
     *  since a table embedded in a shorter container wants neither. */
    className?: string;
}

/** Placeholder for a fact an service that has never connected cannot have. */
function Unknown() {
    return <span className="text-muted-foreground">—</span>;
}

export function LocationsTable({
    workspaceId,
    locations,
    isLoading,
    isError,
    onRetry,
    sort,
    onSortChange,
    sortableColumnKeys,
    pagination,
    emptyBecauseFiltered,
    className,
}: LocationsTableProps) {
    const navigate = useNavigate();
    // One clock for the whole table, so the status column and the last-seen
    // column can never disagree about what "now" is.
    const now = useNow();
    // kore matches a sort against the column key, so the two sortable columns
    // are keyed by the server's own `LocationSortField` names. The other two keep
    // lowercase keys precisely because no wire sort field answers to them.
    const isSortable = (key: string) => sortableColumnKeys?.has(key) === true;

    const columns: DataTableColumn<LocationWithHealth>[] = [
        {
            key: "Name",
            header: sortableHeader("Name", isSortable("Name")),
            sortable: isSortable("Name"),
            cell: (location) => <span className="font-medium">{location.name}</span>,
        },
        {
            key: "status",
            header: sortableHeader("Posting service", isSortable("status")),
            sortable: isSortable("status"),
            cell: (location) => <ServiceStatusBadge status={serviceHealthStatus(location.serviceLastSeenAt, now)} />,
        },
        {
            key: "LastSeen",
            header: sortableHeader("Last seen", isSortable("LastSeen")),
            sortable: isSortable("LastSeen"),
            cell: (location) =>
                location.serviceLastSeenAt ? (
                    <span className="text-muted-foreground">
                        <FormatRelativeTime value={location.serviceLastSeenAt} now={now} />
                    </span>
                ) : (
                    <Unknown />
                ),
        },
        {
            key: "version",
            header: sortableHeader("Version", isSortable("version")),
            sortable: isSortable("version"),
            cell: (location) =>
                location.serviceVersion ? (
                    <span className="tabular-nums text-muted-foreground">{location.serviceVersion}</span>
                ) : (
                    <Unknown />
                ),
        },
    ];

    return (
        <DataTable
            className={className}
            // py-2 rows, not kore's default py-3. One density for every table
            // on the platform; the library default is the roomier one.
            density="compact"
            caption="Locations"
            columns={columns}
            rows={locations}
            rowKey={(location) => location.id}
            // Error wins over loading: a retry leaves the previous failure on
            // screen while it flies, and "loading" would blank the message
            // the user is deciding about.
            state={isError ? "error" : isLoading ? "loading" : undefined}
            error={{
                title: "Couldn't load locations",
                detail: "Nothing was filtered out — the list itself didn't load.",
                onRetry,
            }}
            sort={sort}
            onSortChange={onSortChange}
            pagination={pagination}
            empty={
                emptyBecauseFiltered
                    ? {
                          headline: "No locations match",
                          body: "Try a different search, or clear a filter.",
                      }
                    : {
                          headline: "No locations yet",
                          body: "Add a location to start receiving postings from its POS terminal.",
                      }
            }
            onRowClick={(location) =>
                void navigate({
                    to: "/w/$workspaceId/locations/$locationId",
                    params: { workspaceId, locationId: location.id },
                })
            }
        />
    );
}
